import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Wallet } from 'lucide-react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';
import { EarningsSummary, DailyBars, SeriesTable, TradesTable } from './EarningsParts';

const PERIODS = [['today', 'Bugün'], ['yesterday', 'Dün'], ['7d', '7 gün'], ['30d', '30 gün'], ['month', 'Bu ay'], ['prev_month', 'Geçen ay'], ['all', 'Tümü'], ['custom', 'Tarih aralığı']];
const BOTS = [['main', '🤖 Normal bot'], ['scalper', '⚡ Hızlı Kâr Botu'], ['range', '📐 Aralık Botu']];
const BOT_TR = { main: 'Normal bot', scalper: 'Hızlı Kâr Botu', range: 'Aralık Botu' };
const MODES = [['real', '🏦 Gerçek'], ['paper', '🧪 Sanal']];

function Seg({ items, value, onChange, testId }) {
    return (
        <div className="flex flex-wrap rounded-md border border-slate-700 overflow-hidden" data-testid={testId}>
            {items.map(([k, l]) => (
                <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
                        className={`px-2.5 h-7 text-[11px] font-bold transition-colors ${value === k ? 'bg-emerald-500 text-slate-950' : 'text-slate-300 hover:bg-slate-800'}`}>{l}</button>
            ))}
        </div>
    );
}

/** 💰 Kazanç raporu: bot × defter × dönem; özet, günlük/aylık seri, işlem listesi. */
export default function EarningsPage({ initialBot = 'main', initialMode = 'real', onSelect }) {
    const [bot, setBot] = useState(initialBot);
    const [mode, setMode] = useState(initialMode);
    const [period, setPeriod] = useState('today');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const params = useMemo(() => ({ bot, mode, period, ...(period === 'custom' && from ? { from, to: to || from } : {}) }), [bot, mode, period, from, to]);
    const load = useCallback(async () => {
        if (period === 'custom' && !from) return;
        try {
            const { data: d } = await apiClient.get('/stats/pnl', { params });
            setData(d);
            setErr(null);
        } catch (e) {
            logger.warn('pnl report failed', e);
            setErr('Kazanç raporu yüklenemedi');
        }
    }, [params, period, from]);
    useEffect(() => {
        load();
        const id = setInterval(load, 60000);
        return () => clearInterval(id);
    }, [load]);

    return (
        <div className="space-y-3" data-testid="earnings-page">
            <div className="flex flex-wrap items-center gap-2">
                <Wallet className="h-4 w-4 text-emerald-300" />
                <Seg items={BOTS} value={bot} onChange={setBot} testId="earnings-bot" />
                <Seg items={MODES} value={mode} onChange={setMode} testId="earnings-mode" />
                <Seg items={PERIODS} value={period} onChange={setPeriod} testId="earnings-period" />
                {period === 'custom' && (
                    <div className="flex items-center gap-1 text-[11px] text-slate-300" data-testid="earnings-custom">
                        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-7 rounded border border-slate-700 bg-slate-900 px-1.5 text-slate-100" data-testid="earnings-from" />
                        <span>–</span>
                        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-7 rounded border border-slate-700 bg-slate-900 px-1.5 text-slate-100" data-testid="earnings-to" />
                    </div>
                )}
                {!data && !err && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                {err && <span className="text-[11px] text-rose-300" data-testid="earnings-error">{err}</span>}
            </div>
            {data && (
                <>
                    <div className="text-xs text-slate-400" data-testid="earnings-label">
                        <b className="text-slate-100">{data.label}</b> · {BOT_TR[bot] || bot} · {mode === 'real' ? 'gerçek' : 'sanal'} defter · {data.summary.trades} kapanan işlem
                        {data.summary.tp_but_loss > 0 && <span className="text-amber-300"> · ⚠ {data.summary.tp_but_loss} işlem "hedef" diye kapanıp zarar yazdı (giriş kayması) — zarar sayılır</span>}
                    </div>
                    <EarningsSummary s={data.summary} />
                    <DailyBars series={data.daily} />
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <SeriesTable title="Günlük" rows={data.daily} testId="earnings-daily" />
                        <SeriesTable title="Aylık" rows={data.monthly} testId="earnings-monthly" />
                    </div>
                    <TradesTable rows={data.trades} bot={bot} onSelect={onSelect} />
                </>
            )}
        </div>
    );
}
